import { figma } from './figma';

/**
 * Color primitives — the raw design ramp (values only, no meaning). Anything semantic belongs in
 * tokens/semantics.ts.
 *
 * ⚠️ PALETTE MIGRATION (Figma "default" export). As of the migration this module is an **adapter**:
 * it keeps its historical shape (the keys 51 files + the theme already consume) but sources every
 * value from the canonical Figma palette in `tokens/figma.ts`. That flips the whole app onto the
 * Figma colours by editing one file, with zero churn to consumers — and isolates every colour
 * decision here for review. Each entry documents its old→new remap.
 *
 * Steps that the Figma ramp re-spaced are mapped to the nearest Figma value (approved: nearest-step
 * / by-intent). A later cleanup can rename these legacy keys to the Figma structure (see figma.ts),
 * but that touches every `theme.palette.custom.*` consumer and is deliberately out of scope here.
 */
export const primitives = {
  /** Pure surfaces — the only place '#ffffff' / '#000000' should be written. */
  common: {
    white: figma.common.white,
    black: figma.common.black,
  },

  /**
   * Text-ink ramp — Figma `colors/grey/*`. NEW group (additive): lets `semantics.text` reference the
   * Figma text tokens directly and fixes the historical secondary/tertiary transposition.
   */
  grey: {
    primary: figma.grey.primary, // #2C3A42
    secondary: figma.grey.secondary, // #414E55
    tertiary: figma.grey.tertiary, // #6B757B
    placeholder: figma.grey.placeholder, // #899499
    disabled: figma.grey.disabled, // #B0B5B8
  },

  /**
   * Info / sky accent — Figma has no separate info family (info = the brand teal), so both are
   * folded onto the brand ramp. Was sky-blue #4FB3D9 / #1A5490.
   */
  info: {
    light: figma.brand[50], // → #A6D9E1
    dark: figma.brand[150], // → #055E68
  },

  neutrals: {
    /** Body ink. Values unchanged (they already were the Figma grey trio). */
    textBody: {
      0: figma.grey.primary, // #2C3A42
      10: figma.grey.secondary, // #414E55
      20: figma.grey.tertiary, // #6B757B
    },
    /** #00343C at decreasing opacity → Figma `neutral-opacity` (base identical; nearest alpha). */
    opacity: {
      0: figma.neutralOpacity[0], // solid #00343C
      10: figma.neutralOpacity[90], // 0.95 → 0.9
      20: figma.neutralOpacity[90], // 0.90
      30: figma.neutralOpacity[80], // 0.80
      40: figma.neutralOpacity[70], // 0.70
      50: figma.neutralOpacity[60], // 0.60
      60: figma.neutralOpacity[50], // 0.50
      70: figma.neutralOpacity[40], // 0.40
      80: figma.neutralOpacity[30], // 0.30
      90: figma.neutralOpacity[20], // 0.20
      100: figma.neutralOpacity[10], // 0.10
      110: figma.neutralOpacity[5], // 0.05
    },
    /** Neutral surface/border ramp → Figma `neutral-solid` by step position (0=darkest, 120=lightest). */
    solid: {
      0: figma.neutralSolid[0], // #00343C
      10: figma.neutralSolid[20], // no ns[10] → nearest
      20: figma.neutralSolid[20], // #1A5159
      30: figma.neutralSolid[40], // no ns[30] → nearest
      40: figma.neutralSolid[40], // #33656C
      50: figma.neutralSolid[50], // #4E797F
      60: figma.neutralSolid[60], // #6A8D93
      70: figma.neutralSolid[70], // #85A2A6
      80: figma.neutralSolid[80], // #A0B7BA
      90: figma.neutralSolid[90], // #D6E0E2
      100: figma.neutralSolid[100], // #EAF0F1
      110: 'rgba(234, 240, 241, 0.5)', // ns[100] @ 50% — light wash
      120: figma.neutralSolid[120], // #F8F8FA
    },
  },

  /** Brand teal → Figma `colors/brand/*` (value-nearest; scales are numbered differently). */
  brand: {
    10: figma.brand[10], // #EFF9FA
    25: figma.brand[20], // #DCEFF2
    50: figma.brand[20], // was #DBF1F4 → #DCEFF2
    75: figma.brand[35], // was #B6E3EA → #C3E4EA
    /** `brand/border/default` — the hairline on a selected card, one step off `100`. */
    80: figma.brand[80], // #69C4D2
    85: figma.brand[50], // was #A6D9E1 (exact match)
    100: figma.brand[100], // #49BACA
    120: figma.brand[125], // was #3A95A2 → #1B8B99
    125: figma.brand[125], // was #2C7079 → #1B8B99
    128: figma.brand[175], // was #003E47 → #003239
    150: figma.brand[150], // was #1D4A51 → #055E68
    175: figma.brand[175], // was #203133 → #003239
  },

  green: {
    10: figma.green[10], // #F2F9EF
    /** #DCEAD4 — `positive-green/subtle/border`, the hairline drawn on `10`. */
    20: figma.green[20],
    25: figma.green[50], // was #AACB8F → #A5C891
    50: figma.green[65], // was #93BF6F → #88B66C
    75: figma.green[80], // was #80B158 → #6EA748
    100: figma.green[100], // #569720
    125: figma.green[125], // was #45791A → #3C7500
    150: figma.green[150], // was #345B13 → #295400
    175: figma.green[175], // was #223C0D → #183302
  },

  red: {
    25: figma.red[10], // was #FFEEEE → #FFF4F3
    50: figma.red[20], // was #FFDDDD → #FFE0DD
    /** #FFC8C3 — the hairline on a validation-flagged cell; the only step between
     *  `50` and `75`, and it exists because Figma draws that border and nothing else
     *  on the ramp is it (`39226:52513`). */
    60: figma.red[35],
    75: figma.red[65], // was #FF9898 → #FF928B
    100: figma.red[100], // #FF5454
    125: figma.red[125], // was #AA3232 → #CA2F34
    150: figma.red[150], // was #802222 → #8E1B20
    175: figma.red[175], // was #661B1B → #540B0E
  },

  yellow: {
    10: figma.yellow[20], // was #FEF3D1 → #FAF0D7
    /** #FBDF9E — the hatch stripe drawn ON `10`; the only step between the two. */
    15: figma.yellow[50],
    25: figma.yellow[65], // was #FDDC75 → #FCD57A
    100: figma.yellow[100], // #FCC419
    /** `warning-yellow/filled/icon` — the fill of a filled ⚠, too dark for `100`. */
    110: figma.yellow[125], // #C68400
    125: figma.yellow[150], // was #97760F → #874E00
    150: figma.yellow[175], // was #654E0A → #452000
  },

  orange: {
    25: figma.orange[20], // was #FCE5D0 → #FDE4D5
    50: figma.orange[65], // was #f7b273 → #F8A76F
    100: figma.orange[100], // #F17F16
    125: figma.orange[150], // was #914C0D → #7D3C00
    150: figma.orange[175], // was #603309 → #471E00
  },

  darkBlue: {
    /** The subtle fill an info callout is drawn on (import wizard `toast-slot`). */
    10: figma.darkBlue[10], // #E0EFFF
    25: figma.darkBlue[20], // was #C9DEF4 → #CFE6FF
    100: figma.darkBlue[100], // #2B5888
    /** The ink drawn on `darkBlue[10]` — legible where `100` would not be. */
    125: figma.darkBlue[125], // #204974
    150: figma.darkBlue[150], // was #123357 → #1D3B5B
  },

  purple: {
    25: figma.purple[20], // was #B9A8EB → #DFE0FF
    75: figma.purple[50], // was #8670C7 → #908FD5
    100: figma.purple[100], // #3F1DA4
    150: figma.purple[150], // was #190C42 → #2F1F76
  },

  pink: {
    10: figma.pink[10], // #F9E5FF — the Review table's `Side` change tag
    50: figma.pink[50], // was #EA97FF → #ECB4FC
    100: figma.pink[100], // was #DF63FF → #DA6BF8
    125: figma.pink[125], // was #D52FFF → #A844C2
    150: figma.pink[150], // #742A87 — ink of the `Side` change tag
    175: figma.pink[175], // was #58136A → #41124D
  },
} as const;
